import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Fade,
} from "@mui/material";
import styled from "styled-components";
import tw from "twin.macro";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Sector,
} from "recharts";
import { useTranslation } from "react-i18next";

import BgTitle from "components/title/BgTitle";

// MUI Icons
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Styled Components
const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-10`;

const TabsContainer = styled(Paper)`
  ${tw`bg-white rounded-lg shadow-sm mb-6 overflow-hidden`}
  border: 1px solid #e5e7eb;
`;

const StyledTabs = styled(Tabs)`
  background-color: white;
  border-radius: 20px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-top: -25px;
  position: relative;
  z-index: 10;

  & .MuiTab-root {
    min-height: 45px;
    border-radius: 16px;
    font-size: 0.7rem;
    font-weight: 500;
    padding: 6px 8px;
    min-width: 0;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    @media (max-width: 400px) {
      font-size: 0.65rem;
      padding: 6px 6px;
    }
  }

  & .Mui-selected {
    background-color: #e6f2ff;
    color: #0c4d96;
  }

  & .MuiTabs-indicator {
    display: none;
  }
`;

const ContentContainer = styled(motion.div)`
  ${tw`w-full bg-white rounded-lg shadow-md overflow-hidden`}
  border: 1px solid #e5e7eb;
`;

const FAQList = styled(List)`
  padding: 0;
  width: 100%;
`;

const StyledDivider = styled(Divider)`
  margin: 0 16px;
  opacity: 0.7;
`;

const FAQListItem = styled(motion.div)`
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
  padding-top: 5px;
  padding-bottom: 5px;

  &:hover {
    background-color: #f8fafc;
  }

  &.active {
    background-color: #f1f8fe;
    border-left-color: orange;
  }
`;

const FAQListItemContent = styled(ListItem)`
  padding: 12px 12px;
  cursor: pointer;
  align-items: flex-start;
  gap: 8px;
  
  @media (max-width: 400px) {
    padding: 10px 8px;
    gap: 6px;
  }
`;

const FAQIcon = styled(ListItemIcon)`
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & .MuiSvgIcon-root {
    color: #3b82f6;
    font-size: 1.1rem;
  }
  
  @media (max-width: 400px) {
    min-width: 28px;
    
    & .MuiSvgIcon-root {
      font-size: 1rem;
    }
  }
`;

const FAQQuestion = styled(ListItemText)`
  /* Let the text shrink and wrap between the icon and expand chevron */
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  margin: 0;

  & .MuiListItemText-primary {
    font-weight: 600;
    font-size: 0.9rem;
    line-height: 1.5;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;
    display: block;
    hyphens: auto;
    
    @media (max-width: 400px) {
      font-size: 0.82rem;
      line-height: 1.45;
    }
  }
`;

const ExpandIcon = styled(Box)`
  color: ${(props) => (props.expanded ? "#3b82f6" : "#94a3b8")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
  margin-left: 4px;
`;

const ChartContainer = styled(motion.div)`
  ${tw`w-full py-4 px-2 mx-auto`}
  max-width: 100%;
  height: 380px;
  overflow: visible; /* allow labels to extend beyond container on mobile */

  @media (max-width: 768px) {
    height: auto; /* allow stacked images + chart to define height on mobile */
    padding-left: 0;
    padding-right: 0;
  }
`;

const AnswerContainer = styled(motion.div)`
  background-color: #f8fafc;
  overflow: hidden;
`;

const AnswerInnerContainer = styled(motion.div)`
  ${tw`p-5`}
  padding-top: 0;
  background-image: linear-gradient(
    to bottom,
    rgba(241, 245, 249, 0.8),
    rgba(248, 250, 252, 0.4)
  );
`;

const MultiAnswerContainer = styled.div`
  ${tw`space-y-3 mt-2`}
  max-width: 100%;
  margin: 0 auto;
  font-size: 0.9rem;
  word-break: break-word;
  overflow-wrap: break-word;
  padding: 0 4px;
  
  @media (max-width: 400px) {
    font-size: 0.85rem;
  }
`;

const AnswerItem = styled.div`
  ${tw`pb-2`}
  &:not(:last-child) {
    ${tw`border-b border-gray-100 mb-2`}
  }
`;

const QAResponse = styled(Typography)`
  ${tw`text-gray-700`}
  line-height: 1.7;
  font-size: 0.9rem;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  
  @media (max-width: 400px) {
    font-size: 0.85rem;
    line-height: 1.6;
  }
`;

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82ca9d",
];

// Animation variants
const listItemVariants = {
  hidden: { opacity: 0, y: 5 },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: custom * 0.05,
      duration: 0.3,
      ease: "easeOut",
    },
  }),
};

const answerVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.4,
      ease: "easeInOut",
      when: "beforeChildren",
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
      when: "afterChildren",
    },
  },
};

const answerContentVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      delay: 0.1,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

const chartVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: "easeIn",
    },
  },
};

// Main Component
const RecruitmentQA = () => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [chartKey, setChartKey] = useState(0);
  const [chartReady, setChartReady] = useState(true);
  const chartBoxRef = useRef(null);
  const [mobileSize, setMobileSize] = useState({ w: 0, h: 0 });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumUp = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();

  // Remount the chart after expansion/tab/screen-size changes so ResponsiveContainer recalculates
  useEffect(() => {
    // On small screens, wait for expand animation to finish before mounting chart
    const delay = isMediumUp ? 0 : 650;
    setChartReady(isMediumUp);
    const timer = setTimeout(() => {
      setChartReady(true);
      setChartKey((k) => k + 1);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId, tabValue, isMediumUp]);

  // Measure mobile chart container size to avoid ResponsiveContainer zero-width issue
  useEffect(() => {
    if (isMediumUp) return; // desktop uses ResponsiveContainer
    const el = chartBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr)
        setMobileSize({ w: Math.floor(cr.width), h: Math.floor(cr.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMediumUp]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setExpandedId(null);
    setActiveIndex(0);
  };

  const handleToggleQuestion = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setActiveIndex(0);
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  // Active shape for desktop with pointer lines
  const renderActiveShape = (props) => {
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      value,
      name,
    } = props;

    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // Calculate positions for label lines
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;

    const textAnchor = cos >= 0 ? "start" : "end";

    // On mobile, only highlight the sector (no extra label/line) to avoid duplicate labels with custom label renderer
    if (!isMediumUp) {
      return (
        <g>
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
            stroke="#ffffff"
            strokeWidth={2}
          />
          <Sector
            cx={cx}
            cy={cy}
            startAngle={startAngle}
            endAngle={endAngle}
            innerRadius={outerRadius + 4}
            outerRadius={outerRadius + 8}
            fill={fill}
          />
        </g>
      );
    }

    return (
      <g>
        {/* Highlight the sector */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke="#ffffff"
          strokeWidth={2}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />

        {/* Label line */}
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
          strokeWidth={2}
        />

        {/* Label text */}
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333333"
          style={{ fontWeight: 500, fontSize: "13px" }}
        >
          {name}
        </text>

        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#666666"
          style={{ fontWeight: 400, fontSize: "12px" }}
        >
          {`${value}%`}
        </text>
      </g>
    );
  };

  // Custom render label for desktop view at all times
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
    name,
    value,
  }) => {
    // Draw pointer-line labels on all screen sizes; use tighter spacing and smaller fonts on mobile
    const RADIAN = Math.PI / 180;
    // Use smaller offsets for very small screens (iPhone 8 = 375px)
    const isVerySmall = !isMediumUp && mobileSize.w && mobileSize.w < 380;
    const lineOffset = isMediumUp ? 25 : (isVerySmall ? 8 : 12); // middle point offset
    const labelOffset = isMediumUp ? 35 : (isVerySmall ? 12 : 16); // end label offset
    const fontTitle = isMediumUp ? 13 : (isVerySmall ? 8 : 10);
    const fontValue = isMediumUp ? 12 : (isVerySmall ? 7 : 9);

    const x = cx + (outerRadius + labelOffset) * Math.cos(-midAngle * RADIAN);
    const y = cy + (outerRadius + labelOffset) * Math.sin(-midAngle * RADIAN);
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + outerRadius * cos;
    const sy = cy + outerRadius * sin;
    const mx = cx + (outerRadius + lineOffset) * cos;
    const my = cy + (outerRadius + lineOffset) * sin;
    const ex = x;
    const ey = y;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={COLORS[index % COLORS.length]}
          fill="none"
          strokeWidth={1.2}
        />
        <text
          x={ex}
          y={ey}
          dy={-4}
          textAnchor={textAnchor}
          fill="#333333"
          style={{ fontWeight: 500, fontSize: `${fontTitle}px` }}
        >
          {name}
        </text>
        <text
          x={ex}
          y={ey}
          dy={10}
          textAnchor={textAnchor}
          fill="#666666"
          style={{ fontWeight: 400, fontSize: `${fontValue}px` }}
        >
          {`${value}%`}
        </text>
      </g>
    );
  };

  // Function to render chart based on type and screen size
  const renderChart = (data, type, item) => {
    const explanationComments = item?.explanation?.comments || [];

    switch (type) {
      case "pie":
        return (
          <ChartContainer
            variants={chartVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                flexDirection: isMediumUp ? "row" : "column",
                gap: isMediumUp ? 3 : 2,
              }}
            >
              {/* Explanation Cards Container - Clean HTML/CSS instead of images */}
              {explanationComments && explanationComments.length > 0 && (
                <Box
                  sx={{
                    flex: "0 0 auto",
                    width: isMediumUp ? "40%" : "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {explanationComments.map((comment, idx) => (
                    <Paper
                      key={`comment-${idx}`}
                      elevation={0}
                      sx={{
                        position: "relative",
                        padding: 2.5,
                        backgroundColor: "#fffbf0",
                        border: "3px solid #fbbf24",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          bottom: "-12px",
                          left: "20px",
                          width: "0",
                          height: "0",
                          borderLeft: "12px solid transparent",
                          borderRight: "12px solid transparent",
                          borderTop: "12px solid #fbbf24",
                          zIndex: 1,
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "-7px",
                          left: "23px",
                          width: "0",
                          height: "0",
                          borderLeft: "9px solid transparent",
                          borderRight: "9px solid transparent",
                          borderTop: "9px solid #fffbf0",
                          zIndex: 2,
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1f2937",
                          fontSize: isMediumUp ? "0.9rem" : "0.85rem",
                          lineHeight: 1.7,
                          fontWeight: 500,
                          textAlign: "left",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          hyphens: "auto",
                        }}
                      >
                        {comment}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Chart Container */}
              <Box
                sx={{
                  flex:
                    explanationComments &&
                    explanationComments.length > 0 &&
                    isMediumUp
                      ? "1 1 60%"
                      : "1 1 100%",
                  height: isMediumUp
                    ? "380px"
                    : explanationComments && explanationComments.length > 0
                    ? explanationComments.length > 1
                      ? "360px"
                      : "300px"
                    : "320px",
                  minWidth: 0,
                }}
                ref={chartBoxRef}
              >
                {chartReady ? (
                  isMediumUp ? (
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                      key={`rc-md-${expandedId ?? "none"}-${chartKey}`}
                    >
                      <PieChart
                        margin={{ top: 10, right: 30, bottom: 10, left: 30 }}
                      >
                        <Pie
                          data={data}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          fill="#8884d8"
                          paddingAngle={2}
                          dataKey="value"
                          labelLine={true}
                          label={renderCustomizedLabel}
                        >
                          {data.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                              stroke="#ffffff"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => `${value}%`}
                          contentStyle={{
                            borderRadius: "4px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            border: "none",
                            padding: "10px 14px",
                          }}
                        />
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{
                            paddingTop: 10,
                            paddingBottom: 5,
                            fontSize: "12px",
                          }}
                          iconType="circle"
                          iconSize={10}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <PieChart
                      width={mobileSize.w || 320}
                      height={mobileSize.h || 300}
                      key={`rc-sm-${expandedId ?? "none"}-${chartKey}-${
                        mobileSize.w
                      }x${mobileSize.h}`}
                      margin={{ top: 15, right: 15, bottom: 35, left: 15 }}
                    >
                      <Pie
                        activeIndex={activeIndex}
                        activeShape={renderActiveShape}
                        data={data}
                        cx="50%"
                        cy="45%"
                        innerRadius={mobileSize.w && mobileSize.w < 380 ? 20 : 28}
                        outerRadius={mobileSize.w && mobileSize.w < 380 ? 42 : 55}
                        fill="#8884d8"
                        paddingAngle={2}
                        dataKey="value"
                        onMouseEnter={onPieEnter}
                        onClick={onPieEnter}
                        labelLine={true}
                        label={renderCustomizedLabel}
                      >
                        {data.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => `${value}%`}
                        contentStyle={{
                          borderRadius: "4px",
                          boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          border: "none",
                          padding: "10px 14px",
                        }}
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{
                          paddingTop: 8,
                          paddingBottom: 3,
                          fontSize: mobileSize.w && mobileSize.w < 380 ? "10px" : "11px",
                        }}
                        iconType="circle"
                        iconSize={mobileSize.w && mobileSize.w < 380 ? 8 : 10}
                      />
                    </PieChart>
                  )
                ) : (
                  <Box sx={{ width: "100%", height: "100%" }} />
                )}

                {/* Instructions text */}
                {!isMediumUp && (
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      display: "block",
                      color: "#64748b",
                      mt: 0,
                      mb: 1,
                      fontStyle: "italic",
                      textAlign: "center",
                      width: "100%",
                      fontSize: "0.7rem",
                    }}
                  >
                    {t("recruitment-process-content.faq.chart-instruction") ||
                      "Tap on segments to see details"}
                  </Typography>
                )}
              </Box>
            </Box>
          </ChartContainer>
        );
      default:
        return null;
    }
  };

  // Render FAQs in a list format
  const renderFAQList = () => {
    const questions =
      tabValue === 0
        ? t("recruitment-process-content.employee-questions", {
            returnObjects: true,
          })
        : t("recruitment-process-content.administration-questions", {
            returnObjects: true,
          });

    return (
      <FAQList>
        {questions.map((item, index) => (
          <React.Fragment key={item.id}>
            <motion.div
              initial="hidden"
              animate="visible"
              custom={index}
              variants={listItemVariants}
            >
              <FAQListItem
                className={expandedId === item.id ? "active" : ""}
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
              >
                <FAQListItemContent
                  onClick={() => handleToggleQuestion(item.id)}
                >
                  <FAQIcon>
                    <i
                      style={{ fontSize: "1.5rem", color: "orange" }}
                      className="fa-solid fa-circle-question"
                    ></i>
                  </FAQIcon>
                  <FAQQuestion primary={`${item.question}`} />
                  <ExpandIcon expanded={expandedId === item.id}>
                    {expandedId === item.id ? (
                      <ExpandLessIcon sx={{ color: "orange" }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: "orange" }} />
                    )}
                  </ExpandIcon>
                </FAQListItemContent>
              </FAQListItem>
            </motion.div>

            <AnimatePresence>
              {expandedId === item.id && (
                <AnswerContainer
                  variants={answerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <AnswerInnerContainer variants={answerContentVariants}>
                    {item.chartData ? (
                      renderChart(item.chartData, "pie", item)
                    ) : (
                      <MultiAnswerContainer>
                        {item.answers.map((answer, index) => (
                          <AnswerItem key={index}>
                            <QAResponse variant="body1">
                              {item.answers.length > 1 ? `・${answer}` : answer}
                            </QAResponse>
                          </AnswerItem>
                        ))}
                      </MultiAnswerContainer>
                    )}
                  </AnswerInnerContainer>
                </AnswerContainer>
              )}
            </AnimatePresence>

            {index < questions.length - 1 && <StyledDivider />}
          </React.Fragment>
        ))}
      </FAQList>
    );
  };

  return (
    <Container>
      <Content>
        <Fade in timeout={800}>
          <Box sx={{ mb: 4 }}>
            <BgTitle text={t("recruitment-process-content.faq.title")} />
            <Box
              sx={{
                mt: 2,
                borderLeft: "2px solid #0c4d96",
                borderRight: "2px solid #0c4d96",
                pl: 2,
                backgroundColor: "#e9ecef",
                borderRadius: "12px",
                p: 3,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {t("recruitment.additional_msg_subtitle")}
                <br /> <br />
                {t("recruitment.additional_msg_description")}
              </Typography>
            </Box>
          </Box>
        </Fade>

        {isMobile ? (
          <Box sx={{ mb: 2 }}>
            <StyledTabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
            >
              <Tab
                icon={<QuestionAnswerIcon fontSize="small" />}
                label={t(
                  "recruitment-process-content.faq.tabs.mobile-employees"
                )}
                iconPosition="start"
              />
              <Tab
                icon={<BusinessIcon fontSize="small" />}
                label={t(
                  "recruitment-process-content.faq.tabs.mobile-administration"
                )}
                iconPosition="start"
              />
            </StyledTabs>
          </Box>
        ) : (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <TabsContainer elevation={0}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                centered
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#0c4d96",
                    height: "3px",
                  },
                  "& .Mui-selected": {
                    color: "#0c4d96 !important",
                    fontWeight: "bold",
                    backgroundColor: "rgba(219, 234, 254, 0.4)",
                  },
                  "& .MuiTab-root": {
                    minHeight: "64px",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    letterSpacing: "0.02em",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "rgba(241, 245, 249, 0.8)",
                    },
                  },
                }}
              >
                <Tab
                  label={t("recruitment-process-content.faq.tabs.employees")}
                  icon={
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: tabValue === 0 ? "#dbeafe" : "#f1f5f9",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <QuestionAnswerIcon
                        sx={{
                          fontSize: 16,
                          color: tabValue === 0 ? "#0c4d96" : "#64748b",
                        }}
                      />
                    </Box>
                  }
                  iconPosition="start"
                />
                <Tab
                  label={t(
                    "recruitment-process-content.faq.tabs.administration"
                  )}
                  icon={
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: tabValue === 1 ? "#dbeafe" : "#f1f5f9",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <BusinessIcon
                        sx={{
                          fontSize: 16,
                          color: tabValue === 1 ? "#0c4d96" : "#64748b",
                        }}
                      />
                    </Box>
                  }
                  iconPosition="start"
                />
              </Tabs>
            </TabsContainer>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ContentContainer layout transition={{ duration: 0.5 }}>
            {renderFAQList()}
          </ContentContainer>
        </motion.div>
      </Content>
    </Container>
  );
};

export default RecruitmentQA;
